import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { OrderAttachmentPanel } from "../attachments/OrderAttachmentPanel";
import { GoodsReceiptLinesEditor, emptyGRLine, grLineSktHatasi, type GRLine } from "./GoodsReceiptLinesEditor";
import { trpc } from "../../lib/trpc";
import { istanbulBugun, istanbulGunu, sktOrigin } from "../../lib/sktKurali";

/** Manuel mal kabul fişi DETAYI — künye + kalemler + evrak + DÜZENLE (Tedarikçi/Teslim Eden + dokunulmamışsa kalemler) + tutanak + sil. */
export function ManualReceiptDetailDrawer({ open, receiptId, onClose }: { open: boolean; receiptId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const utils = trpc.useUtils();
  const detailQ = trpc.inventory.receiptDetail.useQuery({ id: receiptId! }, { enabled: open && !!receiptId });
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100 }, { enabled: open });
  const d = detailQ.data;

  const [editing, setEditing] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [editLines, setEditLines] = useState<GRLine[]>([]);
  const [msg, setMsg] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  /** Kalem düzenlemede kaydet denendi → SKT ihlali üst bantta da ("Kalem N: …") gösterilir. */
  const [denendi, setDenendi] = useState(false);

  const updateLinesMut = trpc.inventory.updateReceiptLines.useMutation();
  const updateMut = trpc.inventory.updateReceipt.useMutation();
  const deleteMut = trpc.inventory.deleteGoodsReceipt.useMutation();

  useEffect(() => { if (open) { setEditing(false); setMsg(null); setConfirmDel(false); } }, [open, receiptId]);

  const supplierSSOptions = useMemo<SSOption[]>(() => (suppliersQ.data?.rows ?? []).map((s) => ({ value: s.id, label: s.name, tail: s.code })), [suppliersQ.data]);
  const editLocationId = d?.lines[0]?.locationId ?? "";
  /**
   * Fişin giriş nedeni (receiptDetail). Düzenlemede YENİ eklenen kalem sunucuda bununla denetlenir ve
   * yazılır (açılış stoğu fişine eklenen kalem açılış stoğu — G8/G16); editörün etiketleri de buna uyar.
   */
  const fisOrigin = sktOrigin(d?.origin);

  function enterEdit() {
    if (!d) return;
    setSupplierId(d.supplier?.id ?? "");
    setName(d.deliveredByName ?? "");
    setPhone(d.deliveredByPhone ?? "");
    setNotes(d.notes ?? "");
    // Tarihler İSTANBUL takvim gününe çevrilir (eskiden `.slice(0,10)` = UTC günü): SKT kuralı
    // sunucuda İstanbul günüyle işler ve görüntüleme modu da yerel günü gösterir. Gece 00:00–02:59
    // (İst.) kabul edilen lotun üretim anı UTC'de bir önceki gündür — slice onu bir gün geri
    // gösterir ve kaydedince lot tarihini sessizce kaydırırdı.
    // `mevcut` = lotun KAYITLI hâli: sunucu (updateReceiptLines) mevcut lotu kendi kaynağı ve kabul
    // anıyla denetler, değişmeyen satırı hiç denetlemez — istemci aynası aynı bilgiyle karar versin (G6/G18).
    setEditLines(d.lines.map((l) => {
      const uretimGunu = l.producedAt ? istanbulGunu(l.producedAt) : "";
      const sktGunu = l.expiresAt ? istanbulGunu(l.expiresAt) : "";
      return emptyGRLine({
        lotId: l.lotId,
        mevcut: {
          producedAt: uretimGunu, expiresAt: sktGunu, kabulGunu: istanbulGunu(l.kabulAni), origin: sktOrigin(l.origin),
          varsayilanUretim: !!l.producedAt && Math.abs(new Date(l.producedAt).getTime() - new Date(l.kabulAni).getTime()) < 60_000,
          itemId: l.itemId, itemKind: l.itemKind, lokasyonAyni: !!l.locationId && l.locationId === editLocationId,
        },
        turKey: `kind:${l.itemKind}`, itemId: l.itemId, itemKind: l.itemKind,
        qty: String(l.qty), producedAt: uretimGunu, expiresAt: sktGunu, supplierLot: l.supplierLot ?? "",
      });
    }));
    setMsg(null);
    setDenendi(false);
    setEditing(true);
  }

  /**
   * Düzenlenen kalemlerde İLK SKT kuralı ihlali — sunucunun updateReceiptLines metniyle aynı biçim.
   * "Bugün" çizim anında alınır ve kaydetme kapısı da AYNI değeri kullanır: ekranda görünmeyen bir
   * hatayla mesajsız engel olmasın (gece yarısı dönümü) — ekran geçerli diyorsa sunucu karar verir.
   */
  const sktBugun = istanbulBugun();
  function ilkSktHatasi(): string | null {
    for (const [i, ln] of editLines.entries()) {
      const h = grLineSktHatasi(ln, { mode: "edit", origin: fisOrigin }, sktBugun);
      if (h) return `Kalem ${i + 1}: ${h}`;
    }
    return null;
  }
  // Türetilmiş bant: kaydet denendiyse ve hâlâ ihlal varsa görünür; düzeltilince kendiliğinden kalkar.
  const sktBant = editing && denendi && d?.deletable ? ilkSktHatasi() : null;

  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—");
  const fmtDay = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "—");
  const nf = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 4 });
  const busy = updateLinesMut.isPending || updateMut.isPending;

  async function saveEdit() {
    if (!receiptId || !d) return;
    setMsg(null);
    if (d.deletable) {
      // Kalemler dahil düzenle
      setDenendi(true);
      if (!editLocationId) { setMsg({ tone: "danger", text: "Fişin lokasyonu çözülemedi." }); return; }
      const prepared: Array<{ lotId?: string; itemKind: "stock" | "semi" | "finished"; itemId: string; locationId: string; qty: number; producedAt?: string; expiresAt?: string; supplierLot?: string }> = [];
      for (const [i, ln] of editLines.entries()) {
        if (!ln.itemId || !ln.itemKind) { setMsg({ tone: "danger", text: `Kalem ${i + 1}: ürün seçin.` }); return; }
        if (!ln.qty || Number(ln.qty) <= 0) { setMsg({ tone: "danger", text: `Kalem ${i + 1}: geçerli miktar girin.` }); return; }
        // SKT kuralı ihlali → KAYDETME. Mesaj türetilmiş bantta (sktBant) + alanın altında görünür.
        if (grLineSktHatasi(ln, { mode: "edit", origin: fisOrigin }, sktBugun)) return;
        prepared.push({
          lotId: ln.lotId, itemKind: ln.itemKind, itemId: ln.itemId, locationId: editLocationId, qty: Number(ln.qty),
          producedAt: ln.producedAt ? new Date(ln.producedAt).toISOString() : undefined,
          expiresAt: ln.expiresAt ? new Date(ln.expiresAt).toISOString() : undefined,
          supplierLot: ln.supplierLot || undefined,
        });
      }
      try {
        // Açılış stoğunun tedarikçisi/teslim edeni yoktur (oluşturma formuyla aynı; sunucu da yok sayar — V11).
        const acilis = fisOrigin === "opening_stock";
        await updateLinesMut.mutateAsync({ id: receiptId, supplierId: acilis ? null : supplierId || null, deliveredByName: acilis ? null : name || null, deliveredByPhone: acilis ? null : phone || null, notes: notes || null, lines: prepared });
        await utils.inventory.manualReceipts.invalidate();
        await qc.invalidateQueries();
        setEditing(false);
        setMsg({ tone: "success", text: "Kaydedildi." });
      } catch (e) { setMsg({ tone: "danger", text: (e as Error).message }); }
    } else {
      // Yalnız başlık (kalemler kilitli — stoktan kullanılmış)
      try {
        await updateMut.mutateAsync({ id: receiptId, supplierId: supplierId || null, deliveredByName: name || null, deliveredByPhone: phone || null, notes: notes || null });
        await utils.inventory.manualReceipts.invalidate();
        await qc.invalidateQueries();
        setEditing(false);
        setMsg({ tone: "success", text: "Kaydedildi." });
      } catch (e) { setMsg({ tone: "danger", text: (e as Error).message }); }
    }
  }

  function openTutanak() { if (receiptId) window.open(`/print/mal-kabul-tutanagi?id=${receiptId}`, "_blank", "noopener"); }

  async function doDelete() {
    if (!receiptId) return;
    setMsg(null);
    try {
      await deleteMut.mutateAsync({ id: receiptId });
      await utils.inventory.manualReceipts.invalidate();
      await qc.invalidateQueries();
      onClose();
    } catch (e) {
      const err = e as { data?: { code?: string }; message: string };
      if (err.data?.code === "NOT_FOUND") { await utils.inventory.manualReceipts.invalidate(); onClose(); return; }
      setMsg({ tone: "danger", text: err.message });
      setConfirmDel(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      widthPx={editing ? 1296 : undefined}
      size="xl"
      title={d ? `Mal Kabul · ${d.code}` : "Mal Kabul"}
      subtitle={d ? `${fmtDate(d.receivedAt)}${d.receivedBy ? ` · Teslim alan: ${d.receivedBy}` : ""}` : "Detay yükleniyor…"}
      footer={
        editing ? (
          <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
            <button type="button" className="btn-md primary" onClick={saveEdit} disabled={busy}>{busy ? "Kaydediliyor…" : "Kaydet"}</button>
            <button type="button" className="btn-md ghost" onClick={() => setEditing(false)} disabled={busy}>Vazgeç</button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
            <button type="button" className="btn-md primary" onClick={openTutanak} disabled={!d}><Icon name="fileText" size={14} />Mal Kabul Tutanağı</button>
            <button type="button" className="btn-md secondary" onClick={enterEdit} disabled={!d}><Icon name="edit" size={14} />Düzenle</button>
            <div style={{ flex: 1 }} />
            {confirmDel ? (
              <>
                <span style={{ fontSize: 12.5, color: "var(--danger)", fontWeight: 600 }}>Fiş ve kalemleri silinsin mi?</span>
                <button type="button" className="btn-md danger" onClick={doDelete} disabled={deleteMut.isPending}>{deleteMut.isPending ? "Siliniyor…" : "Evet, Sil"}</button>
                <button type="button" className="btn-md ghost" onClick={() => setConfirmDel(false)} disabled={deleteMut.isPending}>Vazgeç</button>
              </>
            ) : (
              <>
                <button type="button" className="btn-md ghost" style={{ color: d?.deletable ? "var(--danger)" : "var(--neutral-400)", borderColor: d?.deletable ? "var(--danger)" : "var(--neutral-200)" }} onClick={() => setConfirmDel(true)} disabled={!d || !d.deletable} title={d && !d.deletable ? "Kalemler stoktan kullanılmış — silinemez" : "Fişi sil"}>
                  <Icon name="trash" size={14} />Sil
                </button>
                <button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>
              </>
            )}
          </div>
        )
      }
    >
      {detailQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : detailQ.isError ? (
        <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Yüklenemedi</p><p className="sub">{detailQ.error?.message}</p></div>
      ) : !d ? null : editing ? (
        /* ══ DÜZENLEME MODU ══ */
        <>
          {/* Sunucu hatası (msg — SKT BAD_REQUEST dahil; istemcinin bilemediği "Raf ömründen hesaplanan SKT …"
              da buradan gelir) görünür kalır; istemci SKT ihlali yalnız msg yokken. */}
          {msg ? <MsgBand msg={msg} />
            : sktBant ? <div className="form-banner-err" role="alert"><Icon name="alertTriangle" size={14} />{sktBant}</div> : null}
          <div className="form-row">
            {fisOrigin === "opening_stock" ? null : (
              <div className="form-field">
                <label className="lbl">Tedarikçi</label>
                <SearchSelect value={supplierId || null} options={supplierSSOptions} onChange={(v) => setSupplierId(v ?? "")} placeholder="— Tedarikçi (opsiyonel) —" searchPlaceholder="Tedarikçi ara…" />
              </div>
            )}
            <div className="form-field">
              <label className="lbl">Lokasyon</label>
              <div style={{ padding: "9px 11px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 13.5, color: "var(--neutral-700)" }}>{d.locationName ?? "—"}</div>
            </div>
          </div>
          {fisOrigin === "opening_stock" ? null : <div className="form-row">
            <div className="form-field"><label className="lbl">Teslim Eden (Ad Soyad)</label><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Malı teslim eden kişi" /></div>
            <div className="form-field"><label className="lbl">Teslim Eden Telefon</label><input className="form-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={30} placeholder="05xx xxx xx xx" /></div>
          </div>}
          <div className="form-field"><label className="lbl">Not</label><textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} rows={2} placeholder="İrsaliye no, açıklama…" /></div>

          {d.deletable ? (
            <div style={{ marginTop: 8 }}>
              <GoodsReceiptLinesEditor lines={editLines} setLines={setEditLines} enabled={open} origin={fisOrigin} mode="edit" />
            </div>
          ) : (
            <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--info-subtle)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--info-text)", display: "flex", gap: 8, alignItems: "center" }}>
              <Icon name="info" size={15} /><span>Kalemler stoktan kullanılmış — kalem düzenlemesi kilitli. Yalnız başlık bilgileri (tedarikçi/teslim eden/not) güncellenebilir.</span>
            </div>
          )}
        </>
      ) : (
        /* ══ GÖRÜNTÜLEME MODU ══ */
        <>
          {msg ? <MsgBand msg={msg} /> : null}
          {!d.deletable ? (
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "var(--info-subtle)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--info-text)", display: "flex", gap: 8, alignItems: "center" }}>
              <Icon name="info" size={15} /><span>Bu fişin kalemleri stoktan kullanılmış — <b>silinemez / kalem düzenlenemez</b>. Başlık bilgileri düzenlenebilir; kalem hatası için stok düzeltmesi kullanın.</span>
            </div>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "10px 20px", marginBottom: 16 }}>
            <KV k="Fiş No" v={<span className="mono">{d.code}</span>} />
            <KV k="Tarih" v={fmtDate(d.receivedAt)} />
            <KV k="Lokasyon" v={d.locationName ?? "—"} />
            <KV k="Tedarikçi" v={d.supplier?.name ?? "—"} />
            <KV k="Teslim Alan" v={d.receivedBy ?? "—"} />
            <KV k="Kalem Sayısı" v={String(d.lines.length)} />
          </div>

          <div className="d-card" style={{ marginBottom: 16 }}>
            <div className="d-card-head"><span className="d-card-title">Teslim Eden</span></div>
            <div style={{ padding: "12px 14px", display: "flex", flexWrap: "wrap", gap: "6px 24px", fontSize: 13.5 }}>
              <span><b style={{ fontWeight: 600 }}>{d.deliveredByName || "—"}</b></span>
              {d.deliveredByPhone ? <span className="mono" style={{ color: "var(--neutral-600)" }}>{d.deliveredByPhone}</span> : null}
              {d.notes ? <span style={{ color: "var(--neutral-500)", flexBasis: "100%" }}>{d.notes}</span> : null}
            </div>
          </div>

          <div className="dt2-wrap" style={{ marginBottom: 16 }}>
            <div className="dt2-scroll">
              <table className="dt2">
                <thead><tr><th style={{ width: 34 }}>#</th><th>Ürün</th><th className="num" style={{ width: 120 }}>Miktar</th><th style={{ width: 120 }}>SKT</th><th>Lot / Parti</th></tr></thead>
                <tbody>
                  {d.lines.map((l, i) => (
                    <tr key={l.lotId}>
                      <td className="cell-num" style={{ color: "var(--neutral-400)" }}>{i + 1}</td>
                      <td>{l.itemName}<span className="mono" style={{ display: "block", fontSize: 10.5, color: "var(--neutral-400)" }}>{l.itemCode}</span></td>
                      <td className="cell-num">{nf.format(l.qty)} <span style={{ color: "var(--neutral-400)" }}>{l.uom}</span></td>
                      <td>{fmtDay(l.expiresAt)}</td>
                      <td><span className="mono" style={{ fontSize: 11 }}>{l.lotCode}</span>{l.supplierLot ? <span className="mono" style={{ display: "block", fontSize: 10.5, color: "var(--neutral-400)" }}>Parti: {l.supplierLot}</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="dt2-foot"><span>{d.lines.length} kalem · Lokasyon: {d.locationName ?? "—"}</span></div>
          </div>

          <OrderAttachmentPanel goodsReceiptId={d.id} canEdit uploadCategory="receipt" title="Belgeler & Görseller" subtitle="İrsaliye / belge / görseller — bu mal kabul fişine bağlı" />
        </>
      )}
    </Drawer>
  );
}

/** Sonuç bandı — durum = renk + ikon + metin; hata `alert`, başarı `status` olarak okunur. */
function MsgBand({ msg }: { msg: { tone: "success" | "danger"; text: string } }) {
  const hata = msg.tone === "danger";
  return (
    <div className={hata ? "form-banner-err" : "form-banner-ok"} role={hata ? "alert" : "status"}>
      <Icon name={hata ? "alertTriangle" : "checkCircle"} size={14} />{msg.text}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--neutral-500)", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 2 }}>{k}</div>
      <div style={{ fontSize: 13.5, color: "var(--neutral-800)" }}>{v}</div>
    </div>
  );
}
